import {
  Box,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  WarningAmber as WarningAmberIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

function PrecautionsList({
  selectedCategory,
  selectedCategoryId,
  onAddPrecaution,
  onEditPrecaution,
  onDeletePrecaution,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Card
      elevation={3}
      sx={{
        flex: 1,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <WarningAmberIcon sx={{ color: "info.main", fontSize: 28 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              color="text.primary"
              sx={{ lineHeight: 1.2 }}
            >
              {selectedCategory?.category?.name ||
                t("precautions.uncategorized")}
            </Typography>
          </Stack>
          <IconButton
            color="primary"
            onClick={onAddPrecaution}
            disabled={!selectedCategoryId}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                transform: "scale(1.05)",
              },
              "&.Mui-disabled": {
                bgcolor: alpha(theme.palette.grey[400], 0.1),
              },
              transition: "all 0.3s ease",
            }}
            aria-label={t("precautions.addButtonAria")}
          >
            <AddIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        {selectedCategory && selectedCategory.precautions.length > 0 ? (
          <List sx={{ p: 0 }}>
            {selectedCategory.precautions.map((precaution) => (
              <Box key={precaution.id}>
                <Card
                  elevation={1}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: theme.shadows[4],
                      borderColor: "info.main",
                      "& .precaution-actions": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <ListItem
                    sx={{
                      p: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          border: "2px solid",
                          borderColor: alpha(theme.palette.info.main, 0.2),
                          width: 44,
                          height: 44,
                          mr: 2,
                        }}
                      >
                        <WarningAmberIcon
                          sx={{
                            color: "info.main",
                            fontSize: 22,
                          }}
                        />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          fontWeight="500"
                          color="text.primary"
                        >
                          {precaution.name}
                        </Typography>
                      }
                    />
                    <Box
                      className="precaution-actions"
                      sx={{
                        opacity: 0.7,
                        transition: "opacity 0.3s ease",
                        display: "flex",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={t("precautions.editTooltip")}>
                        <IconButton
                          color="primary"
                          onClick={() => onEditPrecaution(precaution)}
                          sx={{
                            borderRadius: 2,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.3s ease",
                          }}
                          aria-label={t("precautions.editButtonAria", {
                            name: precaution.name,
                          })}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("precautions.deleteTooltip")}>
                        <IconButton
                          color="error"
                          onClick={() => onDeletePrecaution(precaution)}
                          sx={{
                            borderRadius: 2,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.3s ease",
                          }}
                          aria-label={t("precautions.deleteButtonAria", {
                            name: precaution.name,
                          })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                </Card>
              </Box>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 3,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.info.main, 0.1),
                mb: 3,
              }}
            >
              <WarningAmberIcon
                sx={{
                  fontSize: 48,
                  color: "info.main",
                  opacity: 0.7,
                }}
              />
            </Box>
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              fontWeight="500"
            >
              {t("precautions.noPrecautions")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("precautions.noPrecautionsDesc")}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default PrecautionsList;
